/**
 * StaffingSocket — small WS wrapper with auto-reconnect (exponential backoff,
 * max 5 attempts), per-event-type listeners, and best-effort send().
 *
 * Usage:
 *   const sock = new StaffingSocket(roomId, 'recruiter', sessionId);
 *   sock.on('question:asked', (msg) => ...);
 *   sock.connect();
 *   sock.send({ type: 'question:asked', ... });
 *   sock.close();
 */
export class StaffingSocket {
  constructor(roomId, role, sessionId) {
    this.roomId = roomId;
    this.role = role;
    this.sessionId = sessionId;
    this.listeners = new Map();
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnect = 5;
    this.closedByUser = false;
  }

  _wsUrl() {
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http/, 'ws');
    const q = `role=${encodeURIComponent(this.role)}&session_id=${encodeURIComponent(this.sessionId)}`;
    return `${base}/api/ws/staffing/room/${encodeURIComponent(this.roomId)}?${q}`;
  }

  connect() {
    this.closedByUser = false;
    try {
      this.ws = new WebSocket(this._wsUrl());
    } catch (err) {
      this._emit('error', err);
      this._scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      this._emit('open');
    };

    this.ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg && msg.type) this._emit(msg.type, msg);
        this._emit('*', msg);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.debug('[StaffingSocket] parse error:', e);
      }
    };

    this.ws.onerror = (ev) => {
      this._emit('error', ev);
    };

    this.ws.onclose = (ev) => {
      this._emit('close', ev);
      this._scheduleReconnect();
    };
  }

  _scheduleReconnect() {
    if (this.closedByUser) return;
    if (this.reconnectAttempts >= this.maxReconnect) return;
    this.reconnectAttempts += 1;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 10000);
    setTimeout(() => {
      if (!this.closedByUser) this.connect();
    }, delay);
  }

  on(type, fn) {
    if (!this.listeners.has(type)) this.listeners.set(type, new Set());
    this.listeners.get(type).add(fn);
    return () => this.listeners.get(type)?.delete(fn);
  }

  _emit(type, payload) {
    const set = this.listeners.get(type);
    if (!set) return;
    set.forEach((fn) => {
      try { fn(payload); } catch (e) { /* swallow */ }
    });
  }

  send(msg) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
      return true;
    }
    return false;
  }

  close() {
    this.closedByUser = true;
    try { this.ws?.close(); } catch { /* noop */ }
  }
}
